import { ArrowBackIosNew, SettingsOutlined, ShoppingCartOutlined } from '@mui/icons-material';
import { Box, IconButton, Stack, Typography, Container, useTheme, Tooltip } from '@mui/material';
import { Link, useNavigate } from 'react-router';
import type { ReactNode } from 'react';
import { useEffect, useLayoutEffect, useState, useMemo, useRef } from 'react';
import { useEventStore } from '@/state/stores/use-event-store';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useActiveOrganizationStore } from '@/state/stores/use-organization-store';
import EditSettingsModal, {
  type MenuButtonType,
} from '@/components/events/detail/tabs/dashboard/settings/edit-settings.modal';
import { format } from 'date-fns';
import useEventMode from '../events/hooks/use-event-mode';
import EventCatalogOrderCheckoutModal from '@/components/events/event-catalog/event-catalog-order-checkout.modal.tsx';
import EventCatalogProductsListModal from '@/components/events/event-catalog/event-catalog-products-list.modal.tsx';
import { useGetEventOrdersQuery } from '@/state/queries/orders/use-get-event-orders.ts';
import { useTranslations } from '@/i18n/use-translations';
import { getOriginEventId } from '@/helpers/event.helper';
import { minuteOffsetToTime } from '@/components/events/helpers/minute-offset-to-time.helper';
import { calculateFlowDurationFromSlots } from '@/components/flows/detail/slot-list/calculate';
import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import { EVENT_CATALOG_ORDERS_FEATURE } from '@/constants/feature-flags';

/** Same rule as slot list: timed content only (excludes gaps and untimed sessions). */
const isUntimedSlot = (slot: FlowSlotDto) =>
  slot.isUntimed ?? (slot.plannedOffsetMinutes === undefined && slot.computedOffsetMinutes === undefined);

interface FixedNavigationBarProps {
  backTo?: string;
  children?: ReactNode;
  context: 'event' | 'flow';
  showBackButton?: boolean;
  /** Renders before checkout/settings in the right section (e.g. START/STOP for flow control) */
  rightActions?: ReactNode;
}

export default function FixedNavigationBar({
  backTo,
  children,
  context,
  showBackButton = true,
  rightActions,
}: FixedNavigationBarProps) {
  const event = useEventStore((s) => s.event);
  const flow = useFlowStore((s) => s.flow);
  const activeOrganization = useActiveOrganizationStore((s) => s.activeOrganization);
  const mode = useEventMode();
  const navigate = useNavigate();
  const [settingsMenuSelected, setSettingsMenuSelected] = useState<MenuButtonType>('general');
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
  const [isCatalogPickerOpen, setIsCatalogPickerOpen] = useState(false);
  const [restoreCheckoutWhenPickerCloses, setRestoreCheckoutWhenPickerCloses] = useState(false);
  const [initialDayIndex, setInitialDayIndex] = useState<number | undefined>(undefined);
  const theme = useTheme();
  const t = useTranslations();
  const { hasAccess: hasCatalogOrderAccess } = useFeatureAccess(EVENT_CATALOG_ORDERS_FEATURE);
  const { data: orders } = useGetEventOrdersQuery(getOriginEventId(event));
  const barRef = useRef<HTMLDivElement>(null);

  // Expose real bar height: body padding only reserves --fixed-topbar-min-height, but tabs + title row are taller.
  useLayoutEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const root = document.documentElement;
    const apply = () => {
      const h = Math.ceil(el.getBoundingClientRect().height);
      root.style.setProperty('--fixed-navigation-bar-height', `${h}px`);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty('--fixed-navigation-bar-height');
    };
  }, []);

  // Listen for custom event to open settings modal from child components
  useEffect(() => {
    const handleOpenSettings = (e?: CustomEvent<{ dayIndex?: number }>) => {
      if (e?.detail?.dayIndex !== undefined) {
        setSettingsMenuSelected('schedule');
        setInitialDayIndex(e.detail.dayIndex);
      } else {
        setSettingsMenuSelected('general');
        setInitialDayIndex(undefined);
      }
      setIsSettingsModalOpen(true);
    };
    window.addEventListener('openEventSettings', handleOpenSettings as EventListener);
    return () => {
      window.removeEventListener('openEventSettings', handleOpenSettings as EventListener);
    };
  }, []);

  // Ensure the global CSS variable reflects visibility while this bar is mounted
  useEffect(() => {
    const root = document.documentElement;
    const previous = root.style.getPropertyValue('--fixed-topbar-visible');
    root.style.setProperty('--fixed-topbar-visible', '1');
    return () => {
      // Restore to previous (or 0) on unmount
      root.style.setProperty('--fixed-topbar-visible', previous || '0');
    };
  }, []);

  const startDate = event?.startDate ? format(new Date(event.startDate), 'dd.MM.yyyy') : undefined;
  const endDate = event?.endDate ? format(new Date(event.endDate), 'dd.MM.yyyy') : undefined;
  const flowStartDate = flow?.startDate ? format(new Date(flow.startDate), 'dd.MM.yyyy') : undefined;

  // Determine title and subtitle based on context
  const title = context === 'event' ? event?.title : event?.multiFlow ? flow?.title || event?.title : event?.title;

  // Extract durationPlannedMinutes to avoid complex expression in dependency array
  const flowDurationPlannedMinutes = flow?.durationPlannedMinutes ?? flow?.slot?.durationPlannedMinutes;

  // Subtitle: event dates for event context, enhanced flow info for flow context
  const subtitle = useMemo(() => {
    if (context === 'event') {
      if (startDate && endDate) {
        return startDate === endDate ? startDate : `${startDate} - ${endDate}`;
      }
      return '';
    }

    // Flow context: show date, start-time, end-time (duration lives in the slot list, not the top bar)
    if (!flowStartDate || flow?.slot?.startOffsetMinutes == null) {
      return flowStartDate;
    }

    const startOffsetMinutes = flow.slot.startOffsetMinutes;
    // Same priority as slot list `displayFlowEndOffset`: fixed end-time wins over slot-derived span.
    const timedSlotsForDuration =
      flow.slots?.filter((slot) => slot.contentType !== 'Gap' && !isUntimedSlot(slot)) ?? [];
    const calculatedFromSlots = calculateFlowDurationFromSlots(timedSlotsForDuration);
    const slotBasedDuration =
      calculatedFromSlots !== undefined && calculatedFromSlots > 0 ? calculatedFromSlots : undefined;
    const hasFixedEndTime = flowDurationPlannedMinutes !== undefined && flowDurationPlannedMinutes > 0;
    const flowDuration = hasFixedEndTime
      ? flowDurationPlannedMinutes
      : (slotBasedDuration ?? flowDurationPlannedMinutes);

    if (flowDuration === undefined || flowDuration <= 0) {
      // If no duration, just show date and start time
      const startTime = minuteOffsetToTime(startOffsetMinutes, flow.startDate);
      return `${flowStartDate}, ${startTime}`;
    }

    const endOffsetMinutes = startOffsetMinutes + flowDuration;
    const startTime = minuteOffsetToTime(startOffsetMinutes, flow.startDate);
    const endTime = minuteOffsetToTime(endOffsetMinutes, flow.startDate);

    return `${flowStartDate}, ${startTime}-${endTime}`;
  }, [context, startDate, endDate, flowStartDate, flow, flowDurationPlannedMinutes]);

  const handleBackClick = () => {
    // Helper function to extract organization ID from backTo URL
    const extractOrgIdFromBackTo = (url: string | undefined): string | null => {
      if (!url) return null;
      const match = url.match(/\/organization\/([^/]+)\/events/);
      return match ? match[1] : null;
    };

    // Since ownedBy is not in the event response, we check if backTo contains an organization
    // route and verify it matches the active organization. If not, navigate to dashboard.
    const orgIdFromBackTo = extractOrgIdFromBackTo(backTo);
    const hasOrgMismatch = orgIdFromBackTo && activeOrganization?.id && orgIdFromBackTo !== activeOrganization.id;
    const hasOrgRouteButNoActiveOrg = orgIdFromBackTo && !activeOrganization;

    // For single-flow events: if organization mismatch or no active org when backTo has org route, go to dashboard
    if (event && !event.multiFlow) {
      if (hasOrgMismatch || hasOrgRouteButNoActiveOrg || (!backTo && !activeOrganization)) {
        navigate('/contributions');
        return;
      }
    }

    // For multi-flow events in event context: if organization mismatch, go to dashboard
    if (event && event.multiFlow && context === 'event') {
      if (hasOrgMismatch || hasOrgRouteButNoActiveOrg) {
        navigate('/contributions');
        return;
      }
    }

    // If backTo is provided and organization matches (or no org route), use it
    if (backTo) {
      navigate(backTo);
    } else if (context === 'flow') {
      // For flow context, navigate based on event type
      if (event?.multiFlow) {
        // Multi-flow event: go to event flows tab
        const eventId = event.origin ? (typeof event.origin === 'string' ? event.origin : event.origin.id) : event.id;
        navigate(`/event/${eventId}?tab=flows`);
      } else {
        // Single-flow event: navigate to organization events list if active organization exists
        if (activeOrganization?.id) {
          navigate(`/organization/${activeOrganization.id}/events`);
        } else {
          navigate('/contributions');
        }
      }
    } else {
      navigate(-1);
    }
  };

  const backButton = (
    <IconButton onClick={handleBackClick} sx={{ p: 0, mr: 1 }}>
      <ArrowBackIosNew sx={{ height: 24, width: 24 }} />
    </IconButton>
  );

  // Show settings only for event context
  const shouldShowSettings = context === 'event';

  // Check if there are items in the cart (draft or payment pending orders)
  const hasCartItems = useMemo(() => {
    if (!orders) return false;
    return orders.some(
      (order) => (order.status === 'DRAFT' || order.status === 'PAYMENT_PENDING') && order.lineItems.length > 0,
    );
  }, [orders]);

  const settingsButton = shouldShowSettings ? (
    <Tooltip title={mode === 'ORGANIZE' ? t('event.switchToPlanToEditSettings') : t('event.settings')}>
      <span>
        <IconButton onClick={() => setIsSettingsModalOpen(true)} disabled={mode === 'ORGANIZE'}>
          <SettingsOutlined sx={{ height: 24, width: 24 }} />
        </IconButton>
      </span>
    </Tooltip>
  ) : null;

  const openCheckoutFromTopBar = () => {
    setIsCatalogPickerOpen(false);
    setRestoreCheckoutWhenPickerCloses(false);
    setIsCheckoutModalOpen(true);
  };

  const handleOpenCatalogPickerFromCheckout = () => {
    setRestoreCheckoutWhenPickerCloses(true);
    setIsCatalogPickerOpen(true);
    setIsCheckoutModalOpen(false);
  };

  const handleCheckoutRequestedFromCatalog = () => {
    setIsCheckoutModalOpen(true);
    setRestoreCheckoutWhenPickerCloses(true);
    setIsCatalogPickerOpen(false);
  };

  const handleCatalogPickerClose = () => {
    setIsCatalogPickerOpen(false);
    setIsCheckoutModalOpen(restoreCheckoutWhenPickerCloses);
  };

  const handleCheckoutModalClose = () => {
    setIsCheckoutModalOpen(false);
    setRestoreCheckoutWhenPickerCloses(false);
    setIsCatalogPickerOpen(false);
  };

  const shoppingCartButton =
    hasCatalogOrderAccess && (context === 'event' || (context === 'flow' && !event?.multiFlow)) && hasCartItems ? (
      <Tooltip title={t('common.checkout')}>
        <IconButton onClick={openCheckoutFromTopBar} sx={{ ml: 1 }} color="primary">
          <ShoppingCartOutlined sx={{ height: 24, width: 24 }} />
        </IconButton>
      </Tooltip>
    ) : null;

  return (
    <Box
      ref={barRef}
      sx={{
        position: 'fixed',
        top: { xs: '50px', md: '64px' },
        left: 0,
        right: 0,
        zIndex: 1100,
        bgcolor: theme.palette.background.topbar,
        minHeight: '48px',
      }}
    >
      <Container sx={{ py: 0, height: '100%' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          height="100%"
          minHeight="48px"
        >
          <Stack direction="row" alignItems="center" sx={{ flexGrow: 1, minWidth: 0 }}>
            {showBackButton && (backTo ? <Link to={backTo}>{backButton}</Link> : backButton)}
            <Stack sx={{ flexGrow: 1, minWidth: 0, justifyContent: 'center', ml: 0 }}>
              <Typography
                variant="h6"
                component="h1"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: subtitle !== undefined ? 1.2 : 1.4,
                }}
              >
                {title || ''}
              </Typography>
              {subtitle !== undefined && (
                <Typography
                  variant="subtitle1"
                  color="textDisabled"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.2,
                  }}
                >
                  {subtitle || ''}
                </Typography>
              )}
            </Stack>
          </Stack>
          <Box sx={{ flexShrink: 0, ml: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              {rightActions}
              {shoppingCartButton}
              {settingsButton}
            </Stack>
          </Box>
        </Stack>
        {children && <Box sx={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>{children}</Box>}
      </Container>
      {(context === 'flow' || context === 'event') && (
        <EditSettingsModal
          open={isSettingsModalOpen}
          menuSelected={settingsMenuSelected}
          setMenuSelected={setSettingsMenuSelected}
          close={() => {
            setIsSettingsModalOpen(false);
            setInitialDayIndex(undefined);
          }}
          initialDayIndex={initialDayIndex}
        />
      )}
      {hasCatalogOrderAccess && (context === 'event' || (context === 'flow' && !event?.multiFlow)) && (
        <>
          <EventCatalogProductsListModal
            event={event}
            open={isCatalogPickerOpen}
            onCheckoutRequested={handleCheckoutRequestedFromCatalog}
            onModalClose={handleCatalogPickerClose}
          />
          <EventCatalogOrderCheckoutModal
            open={isCheckoutModalOpen}
            event={event}
            onModalClose={handleCheckoutModalClose}
            onOpenCatalogPicker={handleOpenCatalogPickerFromCheckout}
          />
        </>
      )}
    </Box>
  );
}
